import { useDispatch, useSelector } from "react-redux";
import {
  addContactRequest,
  addOrganizationRequest,
  deleteContactRequest,
  deleteOrganizationRequest,
  getContactsByOperatorNameRequest,
  getContactsRequest,
  getOrganizationsRequest,
  updateContactRequest,
  updateOrganizationRequest,
} from "../store/phonebookDuck";
import type {
  Contact,
  Organization,
  PhonebookRequestKey,
  PhonebookState,
} from "../types/phonebook";
import type { RootState } from "../store";
import { combineErrors, combineStatuses } from "../utils";

export function usePhonebook() {
  const dispatch = useDispatch();

  const {
    organizations,
    contacts,
    contactsByOperator,
    requests,
  }: PhonebookState = useSelector((state: RootState) => state.phonebook);

  const loadContacts = () => {
    dispatch(getOrganizationsRequest(undefined));
    dispatch(getContactsRequest(undefined));
  };

  const getRequestStatus = (
    request: PhonebookRequestKey | PhonebookRequestKey[]
  ) =>
    useSelector((state: RootState) => {
      const requests = Array.isArray(request) ? request : [request];
      return combineStatuses(
        requests.map((key) => state.phonebook.requests[key].status)
      );
    });

  const getRequestError = (request: PhonebookRequestKey | PhonebookRequestKey[]) =>
    useSelector((state: RootState) => {
      const requests = Array.isArray(request) ? request : [request];

      return combineErrors(
        requests.map((key) => state.phonebook.requests[key].error)
      );
    });

  const getOrganizationMembers = (operatorName: string) => {
    dispatch(getContactsByOperatorNameRequest({ operatorName }));
  };

  const addContact = (contact: Contact) => {
    dispatch(addContactRequest({ contact }));
  };

  const updateContact = (contact: Contact) => {
    dispatch(updateContactRequest({ contact }));
  };

  const deleteContact = (id: number) => {
    dispatch(deleteContactRequest({ id }));
  };

  const addOrganization = (organization: Organization) => {
    dispatch(addOrganizationRequest({ organization }));
  };

  const updateOrganization = (organization: Organization) => {
    dispatch(updateOrganizationRequest({ organization }));
  };

  const deleteOrganization = (id: number) => {
    dispatch(deleteOrganizationRequest({ id }));
  };

  return {
    organizations,
    contacts,
    contactsByOperator,
    requests,
    getRequestStatus,
    getRequestError,
    loadContacts,
    getOrganizationMembers,
    addContact,
    updateContact,
    deleteContact,
    addOrganization,
    updateOrganization,
    deleteOrganization,
  };
}
